import { GetStaticProps } from "next"
import { getFarcasterNotes } from "api_routes/discover"
import { TrendingResp } from "@/types/farcaster"

import { ReactElement, useEffect, useState } from "react"
import DiscoverLayout from "components/layouts/DiscoverLayout"

import { ParsedUrlQuery } from "querystring"
import DiscoverFeed from "components/Discover/DiscoverFeed"
import { useRouter } from "next/router"
import useUser from "hooks/useUser"

import { ProfilesEntry } from "@/types/farcaster"

import { getFarcasterAccount } from "api_routes/discover"
import {
  LoadingContainer,
  TextContainer,
} from "components/Discover/TextContainer"
import { DiscoverCursor } from "@types"

type StaticPropsType = {
  data?: TrendingResp
  errorMsg?: any
}

interface Params extends ParsedUrlQuery {
  identifier: string
}

const fetchData = async (
  params: { cursor?: DiscoverCursor; page?: number; tag?: string },
  address?: string
) => {
  console.log("Fetching data!", params.page, address)
  const res = await getFarcasterNotes(params.page || 1, address)
  return res
}

export const getStaticProps: GetStaticProps<StaticPropsType, Params> = async (
  ctx
) => {
  const vals = ctx
  const params = vals.params
  const id = params?.identifier?.[0]

  // If there's no ID, we show the user
  // that they should log-in.
  if (!id) {
    return {
      props: { data: [] },
      revalidate: 60,
    }
  }

  console.log("Running with ID", id)

  try {
    const res = await fetchData({ page: 1 }, id)

    if ("msg" in res) {
      throw res.msg
    }

    console.log("Got response", res)

    return {
      props: { data: res },
      revalidate: 60,
    }
  } catch (err) {
    console.error("Error fetching Farcaster notes", err)

    return {
      props: { errorMsg: typeof err, data: [] },
      revalidate: 5,
    }
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true,
  }
}

function useHasFcAccount(): ProfilesEntry | false | undefined {
  const router = useRouter()
  const user = useUser()

  const walletInParam = router?.query?.identifier?.[0]

  const usersWallet = "wallet_address" in user ? user.wallet_address : undefined

  // Undefined when loading, false if no account, ProfilesEntry if account.
  const [fcAccount, setFcAccount] = useState<
    ProfilesEntry | undefined | false
  >()

  useEffect(() => {
    async function run() {
      if (router.isReady && !walletInParam && !usersWallet) {
        setFcAccount(false)
        return
      }

      if (!usersWallet && !walletInParam) {
        setFcAccount(undefined)
        return
      }

      const retrievedAccount = await getFarcasterAccount(
        // @ts-ignore
        walletInParam || usersWallet
      )

      setFcAccount(retrievedAccount || false)
    }

    run()
  }, [router.isReady, usersWallet, walletInParam])

  return fcAccount
}

export default function DiscoverFarcasterFeed({
  data,
  errorMsg,
}: StaticPropsType) {
  const router = useRouter()
  const { identifier } = router.query
  const user = useUser()
  const hasFcAccount = useHasFcAccount()

  const usersWallet = "wallet_address" in user ? user.wallet_address : undefined

  if (router.isReady && !identifier && "wallet_address" in user) {
    router.push(`/discover/feed/for-you/farcaster/${user.wallet_address}`)
    return null
  }

  return (
    <div>
      {hasFcAccount === undefined && <LoadingContainer />}
      {!usersWallet && hasFcAccount === false && (
        <TextContainer>
          You're not logged in. <br />
          Log in to see your follower's Paragraph posts.
        </TextContainer>
      )}
      {hasFcAccount === false && usersWallet && (
        <TextContainer>
          You don't have a Farcaster account. <br />
          Create one to see your follower's Paragraph posts.
        </TextContainer>
      )}
      {hasFcAccount && (
        <div>
          <TextContainer>
            <span className="text-sm">
              You're viewing posts created by everyone{" "}
              {hasFcAccount.displayName} ({hasFcAccount.username}) follows on
              Farcaster.
            </span>
          </TextContainer>
          <DiscoverFeed
            data={data}
            fetchData={(params) => fetchData(params, identifier as string)}
            errorMsg={errorMsg}
          />
        </div>
      )}
    </div>
  )
}

DiscoverFarcasterFeed.getLayout = function getLayout(page: ReactElement) {
  return <DiscoverLayout>{page}</DiscoverLayout>
}
